import React, { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LinkIcon from '../assets/copytoClipboard.svg';
import RefreshIcon from '../assets/refresh.svg';
import MoreInfoIcon from '../assets/moreinfo.svg';
import './InstanceControls.css';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DnsIcon from '@mui/icons-material/Dns';

const InstanceControls = ({
  instance,
  timeAgo,
  handleDeleteEnv,
  refreshEnvironment,
  handleResume,
  handlePause,
  handleStart,
  // ...add any other handlers you need
}) => {
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);

  const openPopover = Boolean(anchorEl);

  const handleMoreInfoClick = (event) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);

  const handleDeleteClick = () => {
    setConfirmOpen(true);
    handlePopoverClose();
  };
  const handleConfirmClose = () => setConfirmOpen(false);
  const handleConfirmDelete = () => {
    if (handleDeleteEnv) handleDeleteEnv(instance.envName);
    setConfirmOpen(false);
  };

  const handleMoreInfoTabClick = () => {
    setMoreInfoOpen(true);
    handlePopoverClose();
  };
  const handleMoreInfoModalClose = () => setMoreInfoOpen(false);

  if (!instance) return null;

  // You can use the same getLaunchButtonProps logic from InstanceCard.js
  const getLaunchButtonProps = (status) => {
    switch (status) {
      case 'STARTED':
        return { text: 'Launch', color: 'gradient', enabled: true };
      case 'STOPPED':
      case 'FAILED':
        return { text: 'Re Launch', color: 'failed-gradient', enabled: true };
      case 'PENDING':
        return { text: 'Pending', color: 'pending-bg', enabled: false };
      case 'INSTALLING':
        return { text: 'Installing', color: 'gray', enabled: false };
      case 'RUNNING':
        return { text: 'Running', color: 'gray', enabled: false };
      default:
        return { text: status, color: 'gray', enabled: false };
    }
  };

  const { text, color, enabled } = getLaunchButtonProps(instance.envStatus);

  const handleCopy = () => {
    if (!instance.envExports) return;
    // Find the first string value that starts with http
    let url = null;
    for (const key in instance.envExports) {
      const value = instance.envExports[key];
      if (typeof value === 'string' && value.startsWith('http')) {
        url = value;
        break;
      }
      if (typeof value === 'object' && value !== null) {
        for (const subKey in value) {
          const subValue = value[subKey];
          if (typeof subValue === 'string' && subValue.startsWith('http')) {
            url = subValue;
            break;
          }
        }
      }
      if (url) break;
    }
    if (url) {
      navigator.clipboard.writeText(url);
      // Optionally show a notification
    }
  };

  const handleRefresh = () => {
    if (refreshEnvironment) refreshEnvironment(instance.envName);
  };

  // Helper to extract the URL from envExports (reuse logic from InstanceCard)
  function extractUrlFromEnvExports(envExports) {
    if (!envExports) return null;
    // First, look for URLProxy (case-insensitive)
    for (const key in envExports) {
      if (key.toLowerCase() === 'urlproxy') {
        const value = envExports[key];
        if (typeof value === 'string' && value.startsWith('http')) {
          return value;
        }
      }
      // Also check nested objects for URLProxy
      const value = envExports[key];
      if (typeof value === 'object' && value !== null) {
        for (const subKey in value) {
          if (subKey.toLowerCase() === 'urlproxy') {
            const subValue = value[subKey];
            if (typeof subValue === 'string' && subValue.startsWith('http')) {
              return subValue;
            }
          }
        }
      }
    }
    // Fallback: any string value starting with http (original logic)
    for (const key in envExports) {
      const value = envExports[key];
      if (typeof value === 'string' && value.startsWith('http')) {
        return value;
      }
      if (typeof value === 'object' && value !== null) {
        for (const subKey in value) {
          const subValue = value[subKey];
          if (typeof subValue === 'string' && subValue.startsWith('http')) {
            return subValue;
          }
        }
      }
    }
    return null;
  }

  // Launch button async handler
  const handleLaunch = async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      let envExportsResult = null;
      if ((instance.envStatus === 'FAILED' || instance.envStatus === 'STOPPED') && handleResume) {
        envExportsResult = await handleResume(instance.envName);
      } else if (handleStart) {
        envExportsResult = await handleStart(instance.envName);
      }
      const urlToOpen = extractUrlFromEnvExports(envExportsResult || instance.envExports);
      if (urlToOpen) {
        window.open(`/playground?url=${encodeURIComponent(urlToOpen)}`, '_blank');
      } else {
        // Optionally show a notification here
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="instance-controls">
      <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px', width: '100%', paddingBottom: '10px', alignItems: 'center' }}>
        <div style={{ display: 'flex' , gap:'10px'}}>
          <span className='instance-controls-env-id'>
            <DnsIcon sx={{ fontSize: 16, color: '#A0A0A0' }} />
            {instance.envName}
          </span>
          <span className="instance-controls-env-id">
            <AccessTimeIcon sx={{ fontSize: 16, color: '#A0A0A0' }} />
            {timeAgo(instance.createdAt)}
          </span>
        </div>
      </div>

      <div className="instance-controls-actions">
        <button
          className={`launch-btn ${color}`}
          onClick={handleLaunch}
          disabled={!enabled || loading}
          style={{ width:'200px'}}
        >
          {text}
        </button>
        {instance.envStatus === 'STARTED' && (
          <button
            className="stop-btn"
            onClick={() => handlePause(instance.envName)}
            title="Stop"
            disabled={loading}
            style={{ }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M0 4.57143C0 2.0467 2.0467 0 4.57143 0H27.4286C29.9533 0 32 2.0467 32 4.57143V27.4286C32 29.9533 29.9533 32 27.4286 32H4.57143C2.0467 32 0 29.9533 0 27.4286V4.57143Z" fill="#5F2828" />
              <path d="M16 8C11.5887 8 8 11.5887 8 16C8 20.4113 11.5887 24 16 24C20.4113 24 24 20.4113 24 16C24 11.5887 20.4113 8 16 8ZM16 22C12.692 22 10 19.308 10 16C10 12.692 12.692 10 16 10C19.308 10 22 12.692 22 16C22 19.308 19.308 22 16 22ZM13.3333 13.3333H18.6667V18.6667H13.3333V13.3333Z" fill="white" />
            </svg>
          </button>
        )}

        <Tooltip title="More" arrow placement="top">
          <button className='more-info-icon' onClick={handleMoreInfoClick}>
            <img src={MoreInfoIcon} alt="More Info" className="icon-btn-img" />
          </button>
        </Tooltip>
        <Popover
          open={openPopover}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          classes={{ paper: 'custom-popover-paper' }}
        >
          <MenuItem onClick={handleCopy}>
            <img src={LinkIcon} alt="Copy Link" className="icon-btn-img" style={{ marginRight: 8 }} />
            <ListItemText className='list-item-text-custom' primary="Copy Link" />
          </MenuItem>
          <MenuItem onClick={handleRefresh} disabled={loading}>
            <img src={RefreshIcon} alt="Refresh" className="icon-btn-img" style={{ marginRight: 8 }} />
            <ListItemText className='list-item-text-custom' primary="Refresh" />
          </MenuItem>
          <MenuItem onClick={handleMoreInfoTabClick}>
            <ListItemText className='list-item-text-custom' primary="Instance details" />
          </MenuItem>
          <MenuItem onClick={handleDeleteClick}>
            <ListItemText className='list-item-text-custom' primary="Delete" />
          </MenuItem>
        </Popover>

      </div>

      <Dialog open={confirmOpen} onClose={handleConfirmClose} maxWidth="xs" fullWidth className="confirm-dialog">
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this environment?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={moreInfoOpen} onClose={handleMoreInfoModalClose} maxWidth="sm" fullWidth className="more-info-dialog">
        <DialogTitle>Environment Exports</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 13, background: '#222', color: '#A0A0A0', padding: 16 }}>
            {JSON.stringify(instance.envExports, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstanceControls;
